import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { uniqBy, uniq } from 'lodash';
import {
	Textarea,
	FormItem,
	Select,
	Button,
	Space,
	Modal,
	ModalTitle,
	getBlockItems,
	setBlockItems,
	ParticipantSelect,
	DayPickerInput,
} from '@components';
import MomentLocaleUtils from 'react-day-picker/moment';
import { Agenda, AgendaItem, ParticipantInfo, SummaryBlock, SummaryBlockItem } from '@api';
import './SummaryItemModal.scss';


interface UpdateSummaryItemModalProps {
	closeModal: () => void,
	summaryBlocks: SummaryBlock[],
	summaryBlockIndex: number,
	summaryItemIndex: number,
	summaryAgendaItemId: string,
	agenda: Agenda,
	onSubmit: (updatedSummaryBlocks: SummaryBlock[]) => void,
}

function UpdateSummaryItemModal(props: UpdateSummaryItemModalProps): JSX.Element {
	const {
		closeModal,
		summaryBlocks,
		summaryBlockIndex,
		summaryItemIndex,
		summaryAgendaItemId,
		agenda,
		onSubmit,
	} = props;

	const
		[summaryItemText, setSummaryItemText] = useState<string>(''),
		[selectedSummaryBlock, setSelectedSummaryBlock] = useState<SummaryBlock>(null),
		[selectedSummaryAgendaItem, setSelectedSummaryAgendaItem] = useState<AgendaItem>(null);

	const [selectedPersons, setSelectedPersons] = useState<ParticipantInfo[]>([]);
	const [suggestedPersons, setSuggestedPersons] = useState<ParticipantInfo[]>([]);
	const [selectedDate, setSelectedDate] = useState(null as Date);
	const [suggesteddDates, setSuggesteddDates] = useState<Date[]>([]);

	const agendaItems = useMemo(() => {
		if (agenda) {
			const items: AgendaItem[] = cloneDeep(agenda.items);
			items.unshift(new AgendaItem({ id: null, text: '<Не задана>' }));
			return items;
		} else {
			return [new AgendaItem({ id: null, text: '<Не задана>' })];
		}
	}, [agenda]);

	useEffect(() => {
		const summaryBlock = summaryBlocks[summaryBlockIndex];
		const items = getBlockItems(summaryBlock.__data, summaryAgendaItemId)
		const summaryItem = items[summaryItemIndex];

		setSummaryItemText(summaryItem.text);
		setSelectedSummaryBlock(summaryBlock);
		setSelectedSummaryAgendaItem(agendaItems.filter(ai => ai.id == summaryAgendaItemId)[0]);

		if (summaryItem.selectedPersons && summaryItem.selectedPersons.length) {
			setSelectedPersons(summaryItem.selectedPersons);
		}

		if (summaryItem.persons && summaryItem.persons.length) {
			setSuggestedPersons(uniqBy(summaryItem.persons.filter(p => p.info).map(p => p.info), p => p.id));
		}

		setSelectedDate(summaryItem.selectedDate);
		if (summaryItem.dates) {
			setSuggesteddDates(uniq(summaryItem.dates.filter(d => d.value).map(d => d.value)).slice(0, 3));
		}


	}, [summaryBlocks, summaryBlockIndex, summaryItemIndex, agendaItems,
		summaryAgendaItemId, setSelectedSummaryAgendaItem, setSummaryItemText, setSelectedSummaryBlock]);

	const handleChangeSummaryItemText = useCallback((e): void => {
		setSummaryItemText(e.target.value);
	}, [setSummaryItemText]);

	const handleChangeSelectedSummaryBlock = useCallback((selectedBlock: SummaryBlock): void => {
		setSelectedSummaryBlock(selectedBlock);
	}, [setSelectedSummaryBlock]);

	const handleChangeSelectedAgendaItem = useCallback((selectedAgendaItem: AgendaItem): void => {
		setSelectedSummaryAgendaItem(selectedAgendaItem);
	}, [setSelectedSummaryAgendaItem]);

	const handleSubmit = useCallback((): void => {
		const editSummaryItem = (item: SummaryBlockItem) => {
			item.text = summaryItemText;
			item.isAuto = false;
			item.selectedDate = selectedDate;
			item.selectedPersons = selectedPersons;
		}

		const selectedSummaryBlockIndex = summaryBlocks.indexOf(selectedSummaryBlock);
		if (selectedSummaryBlockIndex == summaryBlockIndex) {
			const updatedBlock: SummaryBlock = cloneDeep(summaryBlocks[selectedSummaryBlockIndex]);
			let initialItems = getBlockItems(updatedBlock.__data, summaryAgendaItemId);
			const summaryItem = initialItems[summaryItemIndex];

			editSummaryItem(summaryItem);
			
			if (selectedSummaryAgendaItem.id != summaryAgendaItemId) {
				const selectedSummaryAgendaItemId = agenda && agenda.showInSummary && updatedBlock.showAgenda
					? selectedSummaryAgendaItem.id : null;
				
				summaryItem.agendaItemId = selectedSummaryAgendaItemId;

				const targetitems = getBlockItems(updatedBlock.__data, selectedSummaryAgendaItemId);
				initialItems = initialItems.filter((_, index) => index != summaryItemIndex)
				targetitems.push(summaryItem);
				setBlockItems(updatedBlock.__data, summaryAgendaItemId, initialItems);
				setBlockItems(updatedBlock.__data, selectedSummaryAgendaItemId, targetitems);
			} else {
				setBlockItems(updatedBlock.__data, summaryAgendaItemId, initialItems);
			}
			updatedBlock.items = updatedBlock.__data.getAllItems();
			summaryBlocks[selectedSummaryBlockIndex] = updatedBlock;
		} else {
			const initialBlock: SummaryBlock = cloneDeep(summaryBlocks[summaryBlockIndex]);
			let initialItems = getBlockItems(initialBlock.__data, summaryAgendaItemId);
			const summaryItem = initialItems[summaryItemIndex];

			editSummaryItem(summaryItem);
			
			const targetBlock: SummaryBlock = cloneDeep(summaryBlocks[selectedSummaryBlockIndex]);
			const selectedSummaryAgendaItemId = agenda && agenda.showInSummary && targetBlock.showAgenda
				? selectedSummaryAgendaItem.id : null;

			summaryItem.agendaItemId = selectedSummaryAgendaItemId;

			const targetItems = getBlockItems(targetBlock.__data, selectedSummaryAgendaItemId);
			targetItems.push(summaryItem);
			initialItems = initialItems.filter((_, index) => index != summaryItemIndex)

			setBlockItems(initialBlock.__data, summaryAgendaItemId, initialItems);
			initialBlock.items = initialBlock.__data.getAllItems();

			setBlockItems(targetBlock.__data, selectedSummaryAgendaItemId, targetItems);
			targetBlock.items = targetBlock.__data.getAllItems();
			
			summaryBlocks[summaryBlockIndex] = initialBlock;
			summaryBlocks[selectedSummaryBlockIndex] = targetBlock;
		}
		
		onSubmit(summaryBlocks);		
		closeModal();
	}, [summaryBlocks, selectedSummaryBlock, summaryBlockIndex, summaryItemIndex, agenda,
		selectedDate, selectedPersons,
		selectedSummaryAgendaItem, summaryAgendaItemId, summaryItemText, closeModal, onSubmit]);

	const headerView = useMemo((): JSX.Element => {
		return (
			<div className="resume-modal__header">
				<ModalTitle onClose={closeModal}>
					Редактирование итога
				</ModalTitle>
			</div>
		);
	}, [closeModal]);

	
	const footerView = useMemo(() => {
		return (
			<Space>
				<Button theme="primary" onClick={handleSubmit}>
					Сохранить
				</Button>
				<Button onClick={closeModal}>
					Отменить
				</Button>
			</Space>
		);
	}, [closeModal, handleSubmit]);

	const showAgendaItems = useMemo(() => {
		return agenda && agenda.showInSummary && selectedSummaryBlock && selectedSummaryBlock.showAgenda;
	}, [agenda, selectedSummaryBlock]);

	const suggestedDatesView = useMemo(() => {
		return suggesteddDates && suggesteddDates.map((d, i) => 
		<div className="suggested-date" key={i} onClick={() => setSelectedDate(d)}>
			{MomentLocaleUtils.formatDate(d, 'LL', 'ru')}
		</div>)
	}, [suggesteddDates]);

	return selectedSummaryBlock && (
		<Modal
			className="resume-modal"
			header={headerView}
			footer={footerView}
			isOpen
		>
			<div className="resume-modal__content">
				<FormItem label="Раздел протокола">
					<Select<SummaryBlock>
						onChange={handleChangeSelectedSummaryBlock}
						textName="blockName"
						value={selectedSummaryBlock}
						options={summaryBlocks}
					/>
				</FormItem>
				<FormItem>
					<Textarea
						autoFocus
						className="resume-modal__textfield"
						value={summaryItemText}
						placeholder="Текст высказывания"
						onChange={handleChangeSummaryItemText}
					/>
				</FormItem>
				<Space gap="16px">
					<FormItem label="Исполнитель" contentClassName='person_box'>
						<ParticipantSelect
							suggestedValues={suggestedPersons}
							values={selectedPersons}
							onChange={setSelectedPersons}
							maxValues={1}
						/>
					</FormItem>
					<FormItem label="Срок" contentClassName='date_box'>
						<DayPickerInput
								value={selectedDate}
								onDayChange={setSelectedDate}
							/>
					</FormItem>
					{suggesteddDates && suggesteddDates.length ?
						<FormItem label="Предлагаемые даты" contentClassName='suggested-date_box'>
							{suggestedDatesView}
						</FormItem>
					: null}					
				</Space>
				
				{showAgendaItems &&
					<FormItem label="Повестка">
						<Select<AgendaItem>
							onChange={handleChangeSelectedAgendaItem}
							textName="text"
							value={selectedSummaryAgendaItem}
							options={agendaItems}
						/>
					</FormItem>
				}
			</div>
		</Modal>
	);
}

export {UpdateSummaryItemModal};